import { fmtDateShort, platformStyle, releaseId } from "../../lib/api";
import { VoteButtons } from "./VoteButtons";
import { AddToCalendar } from "./AddToCalendar";
import { useReleaseModal } from "./ReleaseModal";

const STREAMER_PLATFORMS = new Set([
  "Netflix",
  "Disney+",
  "Prime Video",
  "Apple TV+",
  "Canal+",
  "Max",
  "Arte",
]);
const NEW_WINDOW_DAYS = 30;

export const ReleasePosterCard = ({ r, featured = false, statsById, mineById, hideVotes = false }) => {
  const { openRelease } = useReleaseModal();
  const today = new Date().toISOString().slice(0, 10);
  // `hideVotes` is set by parents rendering the "upcoming" strips. In that
  // context the card must stay coherent (À venir badge + locked vote text),
  // even if release_date happens to equal today due to timezone rounding.
  const isReleased = !hideVotes && r.release_date && r.release_date <= today;
  let platform = r.platforms?.[0] || "À venir";
  // Films with no streaming provider are cinema-only. Once they're out, the
  // "À venir" tag is confusing — display "En salle" instead.
  if (r.type === "Film" && isReleased && platform === "À venir") {
    platform = "En salle";
  }
  const badgeCls = platformStyle[platform] || platformStyle["À venir"];
  const rid = releaseId(r);
  const initialStats = statsById ? statsById[rid] || { up: 0, down: 0, total: 0, score: 0 } : null;
  const initialMine = mineById ? mineById[rid] || null : null;

  // "New · <date>" — signals a fresh arrival on a French streamer.
  // Two conditions must both hold:
  //   1. `release_date` is within NEW_WINDOW_DAYS (the title itself is
  //      genuinely new, not just newly tracked in our DB — this prevents
  //      old back-catalog titles we just started syncing from being
  //      wrongly flagged as fresh).
  //   2. `platform_added_at` is within the same window (it just landed
  //      on the platform's FR catalogue).
  // The displayed date is `release_date` so a single card never shows
  // two conflicting dates — the bottom date (release) and the NEW badge
  // date must always match to avoid visitor confusion.
  let newAddedAt = null;
  if (
    isReleased &&
    r.type === "Série" &&
    r.platform_added_at &&
    r.release_date &&
    STREAMER_PLATFORMS.has(platform)
  ) {
    const now = Date.now();
    const addedAgeDays = (now - new Date(r.platform_added_at).getTime()) / 86400000;
    const releaseAgeDays = (now - new Date(r.release_date).getTime()) / 86400000;
    if (addedAgeDays <= NEW_WINDOW_DAYS && releaseAgeDays <= NEW_WINDOW_DAYS) {
      newAddedAt = r.release_date;
    }
  }

  // Relative "freshness" label — reinforces the fresh-sort strip on the
  // homepage without a heavy redesign. Only rendered for recently released
  // titles (past 14 days) so it doesn't pollute the classic catalogue view.
  let freshLabel = null;
  if (isReleased && r.release_date) {
    const rel = new Date(r.release_date + "T00:00:00Z");
    const now = new Date();
    const diffDays = Math.floor(
      (Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) - rel.getTime()) / 86400000
    );
    if (diffDays === 0) freshLabel = "Sorti aujourd'hui";
    else if (diffDays === 1) freshLabel = "Sorti hier";
    else if (diffDays > 1 && diffDays <= 14) freshLabel = `Sorti il y a ${diffDays} jours`;
  }

  return (
    <article
      data-testid={`release-${r.tmdb_id}`}
      role="button"
      tabIndex={0}
      aria-label={`Voir le détail de ${r.title}`}
      onClick={() => openRelease(r)}
      onKeyDown={(e) => {
        if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          openRelease(r);
        }
      }}
      className={`group cursor-pointer card-lift bg-white border border-[var(--line)] rounded-md overflow-hidden ${
        featured ? "md:col-span-2 md:row-span-2" : ""
      }`}
    >
      <div className="relative aspect-[2/3] bg-neutral-100 overflow-hidden">
        {r.poster_url ? (
          <img
            src={r.poster_url}
            alt={r.title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
          />
        ) : (
          <div className="grid place-items-center h-full text-neutral-400 text-xs font-display italic">
            Pas d'affiche
          </div>
        )}
        <div className="absolute top-2 left-2 flex flex-col items-start gap-1">
          <span
            data-testid={`release-platform-${r.tmdb_id}`}
            className={`inline-flex w-fit items-center text-[9px] uppercase tracking-normal font-semibold px-1.5 py-[2px] rounded-[3px] leading-none ${badgeCls}`}
          >
            {platform}
          </span>
          {newAddedAt && (
            <span
              data-testid={`new-on-platform-${r.tmdb_id}`}
              className="inline-flex w-fit items-center gap-0.5 text-[9px] uppercase tracking-normal font-semibold px-1.5 py-[2px] rounded-[3px] leading-none bg-[var(--red)] text-white shadow-[0_3px_10px_-4px_rgba(200,16,46,0.55)]"
              title={`Sortie sur ${platform} le ${fmtDateShort(newAddedAt)}`}
            >
              <span>New</span>
              <span>· {fmtDateShort(newAddedAt)}</span>
            </span>
          )}
        </div>
        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between gap-2">
          <span className="text-[11px] uppercase tracking-[0.25em] font-medium text-white bg-black/55 backdrop-blur px-2 py-1 rounded-sm">
            {fmtDateShort(r.release_date)}
          </span>
          <span
            data-testid={`release-type-${r.tmdb_id}`}
            className="text-[12px] uppercase tracking-[0.28em] font-bold"
            style={{
              textShadow:
                "0 1px 2px rgba(0,0,0,0.85), 0 0 8px rgba(0,0,0,0.55)",
              color: r.type === "Film" ? "#ff1f3f" : "#ffffff",
              WebkitTextStroke:
                r.type === "Film" ? "0.4px rgba(0,0,0,0.35)" : "0.4px rgba(0,0,0,0.45)",
            }}
          >
            {r.type}
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className={`font-display ${featured ? "text-2xl" : "text-lg"} leading-tight line-clamp-2`} title={r.title}>
          {r.title}
        </h3>
        {r.original_title && r.original_title.trim() && r.original_title.trim().toLowerCase() !== (r.title || "").trim().toLowerCase() && (
          <div
            className="text-[11px] text-neutral-400 italic mt-0.5 line-clamp-1"
            title={r.original_title}
            data-testid="release-original-title"
          >
            {r.original_title}
          </div>
        )}
        {freshLabel && (
          <div
            className="text-[10px] uppercase tracking-[0.2em] text-[var(--red)] mt-1.5 font-medium"
            data-testid="release-fresh-label"
          >
            {freshLabel}
          </div>
        )}
        {r.genres?.length > 0 && (
          <div className="text-[11px] text-neutral-500 mt-1.5 uppercase tracking-[0.15em] truncate">
            {r.genres.slice(0, 3).join(" · ")}
          </div>
        )}
        {r.countries?.length > 0 && (
          <div className="text-[11px] text-neutral-400 mt-0.5 italic">
            {r.countries.slice(0, 2).join(", ")}
          </div>
        )}
        <div className="mt-3 pt-3 border-t border-[var(--line)] cursor-default" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          {hideVotes ? (
            <div className="space-y-1.5">
              <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-400">
                À venir · Vote à l'ouverture
              </div>
              {r.release_date && (
                <div className="flex items-center justify-end gap-2">
                  <span
                    className="text-[11px] font-medium text-[var(--red)] inline-flex items-center gap-1.5"
                    data-testid="calendar-cta-text"
                  >
                    Bloquez la date
                    <span aria-hidden="true" className="text-[var(--red)]">→</span>
                  </span>
                  <AddToCalendar release={r} variant="icon" />
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <VoteButtons
                    release={r}
                    compact
                    initialStats={initialStats}
                    initialMine={initialMine}
                  />
                </div>
                {r.release_date && !isReleased && (
                  <AddToCalendar release={r} variant="icon" />
                )}
              </div>
              {r.release_date && !isReleased && (
                <div className="flex justify-end">
                  <span
                    className="text-[11px] font-medium text-[var(--red)] inline-flex items-center gap-1.5"
                    data-testid="calendar-cta-text"
                  >
                    Bloquez la date
                    <span aria-hidden="true" className="text-[var(--red)]">→</span>
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
};
